/**
 * PokemonCard - Grid card component for Pokemon display
 */
import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Heart } from 'lucide-react';
import { TypeBadgeGroup } from './TypeBadge';
import { useApp } from '../../context/AppContext';
import { usePokemon } from '../../hooks/usePokemon';
import { Skeleton } from '../ui/skeleton';
import { formatPokemonName } from '../../data/pokemonTranslations';

export function PokemonCard({ pokemon, index = 0 }) {
  const { isFavorite, toggleFavorite, language } = useApp();
  const { getPokemon, getSprites } = usePokemon();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [imageLoaded, setImageLoaded] = useState(false);
  
  // Fetch pokemon data
  useEffect(() => {
    let mounted = true;
    
    const fetchData = async () => {
      setLoading(true);
      const pokemonData = await getPokemon(pokemon.id || pokemon.name);
      if (mounted && pokemonData) {
        setData(pokemonData);
      }
      setLoading(false);
    };
    
    fetchData();
    return () => { mounted = false; };
  }, [pokemon.id, pokemon.name, getPokemon]);
  
  const sprites = data ? getSprites(data) : null;
  const types = data?.types?.map(t => t.type.name) || [];
  const pokemonId = data?.id || pokemon.id;
  const favorite = isFavorite(pokemonId);
  
  const handleFavoriteClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    toggleFavorite(pokemonId);
  };
  
  if (loading) {
    return <PokemonCardSkeleton />;
  }
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: index * 0.02 }}
      whileHover={{ y: -4 }}
      className="group"
    >
      <Link 
        to={`/pokemon/${pokemonId}`}
        data-testid={`pokemon-card-${pokemonId}`}
        className="block"
      >
        <div className="relative bg-card rounded-2xl border border-border shadow-sm hover:shadow-xl transition-all duration-300 overflow-hidden">
          {/* ID watermark */}
          <span className="absolute top-3 left-3 text-5xl font-black text-muted-foreground/10 select-none pointer-events-none">
            #{String(pokemonId).padStart(3, '0')}
          </span>
          
          {/* Favorite button */}
          <button
            onClick={handleFavoriteClick}
            data-testid={`favorite-btn-${pokemonId}`}
            className="absolute top-3 right-3 z-10 p-2 rounded-full bg-white/80 dark:bg-black/50 backdrop-blur-sm hover:scale-110 transition-transform"
          >
            <Heart 
              className={`w-5 h-5 transition-colors ${
                favorite 
                  ? 'fill-rose-500 text-rose-500' 
                  : 'text-muted-foreground hover:text-rose-500'
              }`}
            />
          </button>
          
          {/* Pokemon image */}
          <div className="relative pt-8 pb-4 px-4 flex items-center justify-center h-56">
            {!imageLoaded && (
              <div className="absolute inset-0 flex items-center justify-center">
                <Skeleton className="w-40 h-40 rounded-full" />
              </div>
            )}
            <img
              src={sprites?.default}
              alt={data?.name}
              loading="lazy"
              onLoad={() => setImageLoaded(true)}
              className={`w-40 h-40 object-contain drop-shadow-lg group-hover:scale-110 transition-transform duration-300 ${
                imageLoaded ? 'opacity-100' : 'opacity-0'
              }`}
            />
          </div>
          
          {/* Pokemon info */}
          <div className="p-4 pt-0 space-y-2">
            <p className="text-xs font-bold text-muted-foreground uppercase tracking-widest">
              #{String(pokemonId).padStart(3, '0')}
            </p>
            <h3 className="text-lg font-bold text-foreground truncate">
              {formatPokemonName(data?.name, language)}
            </h3>
            <TypeBadgeGroup types={types} size="xs" />
          </div>
        </div>
      </Link>
    </motion.div>
  );
}

export function PokemonCardSkeleton() {
  return (
    <div className="bg-card rounded-2xl border border-border shadow-sm overflow-hidden">
      <div className="pt-8 pb-4 px-4 flex items-center justify-center h-44">
        <Skeleton className="w-32 h-32 rounded-full" />
      </div>
      <div className="p-4 pt-0 space-y-2">
        <Skeleton className="h-3 w-12" />
        <Skeleton className="h-5 w-24" />
        <div className="flex gap-1.5">
          <Skeleton className="h-5 w-14 rounded-full" />
          <Skeleton className="h-5 w-14 rounded-full" />
        </div>
      </div>
    </div>
  );
}

export default PokemonCard;
